import React, { useState, useEffect } from 'react';
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Button,
  Badge,
  Typography,
  Divider
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { Message } from '../../types/message';

export default function UserAvatarMenu() {
  const { currentUser, logout, showAuthDialog, unreadMessages, refreshUnreadMessages } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const notificationsOpen = Boolean(notificationsAnchorEl);
  const navigate = useNavigate();

  // Refresh unread messages count when component mounts if user is logged in
  useEffect(() => {
    if (currentUser && refreshUnreadMessages) {
      refreshUnreadMessages();
    }
    const intervalId = setInterval(refreshUnreadMessages, 60 * 1000); // Refresh every 60 seconds
    return () => clearInterval(intervalId);
  }, [currentUser]);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsMenuClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleSignIn = () => {
    showAuthDialog('login');
  };

  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
    if (refreshUnreadMessages) {
      refreshUnreadMessages();
    }
  };

  const handleMessageClick = (message: Message) => {
    handleNotificationsMenuClose();
    if (message.order?.documentId) {
      if (currentUser && currentUser.userType === 'user') {
        navigate(`/user/order/${message.order.documentId}`);
      } else {
        navigate(`/admin/order/${message.order.documentId}`);
      }
    }
  };

  // Show sign in button when no user is logged in
  if (!currentUser) {
    return (
      <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 1000 }}>
        <Button
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={handleSignIn}
          sx={{ bgcolor: 'primary.main' }}
        >
          Sign In
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 1000, display: 'flex', gap: 1, alignItems: 'center' }}>
      <IconButton
        onClick={handleNotificationsClick}
        size="small"
        aria-label="notifications"
        aria-controls={notificationsOpen ? 'notifications-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={notificationsOpen ? 'true' : undefined}
      >
        <Badge 
          badgeContent={unreadMessages && unreadMessages.length > 0 ? unreadMessages.length : null} 
          color="error"
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <IconButton
        onClick={handleAvatarClick}
        size="small"
        sx={{ ml: 2 }}
        aria-controls={open ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
          {currentUser.firstName?.[0]}{currentUser.lastName?.[0]}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={notificationsAnchorEl}
        id="notifications-menu"
        open={notificationsOpen}
        onClose={handleNotificationsMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'scrollY',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            maxWidth: 400,
            minWidth: 300,
            maxHeight: 400,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {unreadMessages && unreadMessages.length > 0 ? (
          unreadMessages.map((message, index) => (
            <Box key={message.id}>
              <MenuItem 
                onClick={() => handleMessageClick(message)}
                sx={{ 
                  whiteSpace: 'normal',
                  maxWidth: 380,
                  alignItems: 'flex-start',
                  py: 2
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                    {message?.author?.firstName} {message?.author?.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {message.message && message.message.length > 100 ? `${message.message.substring(0, 100)}...` : message.message}
                  </Typography>
                  
                  {message.order && (
                    <Typography variant="caption" color="primary" component={"div"}>
                      Order: {message.order.confirmation || message.order.documentId}
                    </Typography>
                  )}
                  <Typography sx={{fontSize: '70%'}} variant="subtitle2" color="text.secondary" component={"div"}>
                    {new Date(message.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </MenuItem>
              {index < unreadMessages.length - 1 && <Divider />}
            </Box>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No unread messages
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}